import { useState, useEffect, useCallback } from 'react'
import { io, type Socket } from 'socket.io-client'
import { useAuthStore } from '../../auth/useAuthStore'
import { useAppStore } from '../../store/appStore'
import { setCollabRoom } from '../../lib/api'

const WS_URL = ((import.meta as any).env?.VITE_WS_URL ?? 'ws://localhost:3001').replace(/^ws/, 'http')

interface Presence {
  userId: string
  displayName: string
  color: string
  activeFile?: string
  cursorLine?: number
}

let collabSocket: Socket | null = null

export function getCollabSocket() { return collabSocket }
export function getCollabRoomId() { return localStorage.getItem('pulse-collab-room') }

export function CollabPanel() {
  const user = useAuthStore((s) => s.user)
  const token = useAuthStore((s) => s.token)
  const [roomId, setRoomId] = useState<string | null>(() => localStorage.getItem('pulse-collab-room'))
  const [ownerName, setOwnerName] = useState<string | null>(() => {
    const saved = localStorage.getItem('pulse-collab-owner')
    return saved && saved !== 'null' ? saved : null
  })
  const [inviteCode, setInviteCode] = useState('')
  const [joinCode, setJoinCode] = useState('')
  const [peers, setPeers] = useState<Presence[]>([])
  const [connected, setConnected] = useState(false)
  const [copied, setCopied] = useState(false)
  const [status, setStatus] = useState('')

  // Connect socket
  useEffect(() => {
    if (collabSocket) { setConnected(collabSocket.connected); return }
    const s = io(WS_URL, { transports: ['websocket', 'polling'], auth: { token } })
    collabSocket = s
    ;(window as any).__pulseCollabSocket = s

    s.on('connect', () => {
      setConnected(true)
      const savedRoom = localStorage.getItem('pulse-collab-room')
      if (savedRoom && user) {
        s.emit('room:join', { roomId: savedRoom, userId: user.id, displayName: user.name })
        useAppStore.setState({ roomId: savedRoom, isCollabActive: true })
        // Request fresh presence after a short delay to ensure server processed the join
        setTimeout(() => {
          s.emit('room:get-presence', { roomId: savedRoom })
        }, 500)
      }
    })
    s.on('disconnect', () => setConnected(false))
    s.on('room:presence', (others: Presence[]) => setPeers(others))
    s.on('user:joined', (p: Presence) => {
      setPeers((prev) => [...prev.filter((x) => x.userId !== p.userId), p])
      setStatus(`${p.displayName} joined`)
      setTimeout(() => setStatus(''), 3000)
    })
    s.on('user:left', ({ userId }: { userId: string }) => {
      setPeers((prev) => {
        const leaving = prev.find((p) => p.userId === userId)
        if (leaving) { setStatus(`${leaving.displayName} left`); setTimeout(() => setStatus(''), 3000) }
        return prev.filter((x) => x.userId !== userId)
      })
    })
    s.on('presence:update', (data: Presence & { socketId: string }) => {
      setPeers((prev) => prev.map((p) => p.userId === data.userId ? { ...p, ...data } : p))
    })

    // File sync from other users
    s.on('file:changed', ({ path: filePath, content }: { path: string; content: string }) => {
      const state = useAppStore.getState()
      const tab = state.openTabs.find((t) => t.path === filePath || t.path === `/${filePath}`)
      if (tab) {
        ;(window as any).__pulseRemoteIncoming = true
        state.markDirty(tab.id, content)
        Promise.resolve().then(() => { (window as any).__pulseRemoteIncoming = false })
      }
    })

    // Collab generate events
    s.on('generate:file', () => { window.dispatchEvent(new CustomEvent('pulse:files-changed')) })
    s.on('generate:done', () => { window.dispatchEvent(new CustomEvent('pulse:files-changed')) })

    return () => {}
  }, [token])

  // Presence updates
  useEffect(() => {
    if (!collabSocket || !roomId || !user) return
    const unsub = useAppStore.subscribe((state, prev) => {
      if (state.activeTabId !== prev.activeTabId) {
        const tab = state.openTabs.find((t) => t.id === state.activeTabId)
        collabSocket?.emit('presence:update', { roomId, activeFile: tab?.name ?? undefined })
      }
    })
    return unsub
  }, [roomId, user])

  const createRoom = useCallback(() => {
    if (!collabSocket || !user) return
    collabSocket.emit('room:create', { name: `${user.name}'s Room` }, (res: any) => {
      if (res.error) { setStatus(res.error); return }
      setRoomId(res.roomId)
      setInviteCode(res.inviteCode)
      setOwnerName(user.name)
      localStorage.setItem('pulse-collab-owner', user.name)
      setCollabRoom(res.roomId)
      useAppStore.setState({ roomId: res.roomId, isCollabActive: true })
      collabSocket!.emit('room:join', { roomId: res.roomId, userId: user.id, displayName: user.name })
      setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 500)
    })
  }, [user])

  const joinRoom = useCallback(() => {
    if (!collabSocket || !user || !joinCode.trim()) return
    collabSocket.emit('room:join-invite', { inviteCode: joinCode.trim() }, (res: any) => {
      if (res.error) { setStatus(res.error); return }
      setRoomId(res.roomId)
      setOwnerName(res.ownerName ?? 'Unknown')
      localStorage.setItem('pulse-collab-owner', res.ownerName ?? 'Unknown')
      setCollabRoom(res.roomId)
      useAppStore.setState({ roomId: res.roomId, isCollabActive: true })
      collabSocket!.emit('room:join', { roomId: res.roomId, userId: user.id, displayName: user.name })
      // Force file tree to reload with the room owner's workspace
      setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 500)
      setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 1500)
      // Auto-switch to Explorer so user sees the shared files
      window.dispatchEvent(new CustomEvent('pulse:switch-view', { detail: 'files' }))
      setJoinCode('')
    })
  }, [user, joinCode])

  const leaveRoom = useCallback(() => {
    setRoomId(null)
    setOwnerName(null)
    localStorage.removeItem('pulse-collab-owner')
    setCollabRoom(null)
    useAppStore.setState({ roomId: null, isCollabActive: false })
    setPeers([])
    setInviteCode('')
    setStatus('')
    setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 300)
  }, [])

  const copyInvite = () => {
    const link = `${window.location.origin}?collab=${inviteCode}`
    navigator.clipboard.writeText(link)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  // Auto-join from URL param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const code = params.get('collab')
    if (code && collabSocket && user && !roomId) {
      setJoinCode(code)
      window.history.replaceState({}, '', window.location.pathname)
      setTimeout(() => {
        collabSocket!.emit('room:join-invite', { inviteCode: code }, (res: any) => {
          if (res.error) return
          setRoomId(res.roomId)
          setOwnerName(res.ownerName ?? 'Unknown')
          setCollabRoom(res.roomId)
          useAppStore.setState({ roomId: res.roomId, isCollabActive: true })
          collabSocket!.emit('room:join', { roomId: res.roomId, userId: user.id, displayName: user.name })
          setTimeout(() => window.dispatchEvent(new CustomEvent('pulse:files-changed')), 500)
        })
      }, 500)
    }
  }, [user, roomId])

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Connection status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: connected ? 'var(--accent-2)' : 'var(--red)' }} />
        <span style={{ fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--text-2)' }}>{connected ? 'Connected' : 'Disconnected'}</span>
      </div>

      {status && (
        <div style={{ fontSize: 10, fontFamily: 'var(--font-mono)', color: 'var(--accent)', padding: '4px 8px', background: 'rgba(124,110,245,0.06)', borderRadius: 6 }}>
          {status}
        </div>
      )}

      {!roomId ? (
        <>
          <div>
            <div style={sectionLabel}>Start a live session</div>
            <p style={{ fontSize: 10, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', lineHeight: 1.6, marginBottom: 10 }}>
              Code together in real-time. Share files, see live cursors, and generate apps together.
            </p>
            <button onClick={createRoom} style={btnStyle}>+ Create Room</button>
          </div>
          <div style={{ height: 1, background: 'var(--border)' }} />
          <div>
            <div style={sectionLabel}>Join a session</div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input
                value={joinCode}
                onChange={(e) => setJoinCode(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') joinRoom() }}
                placeholder="Paste invite code..."
                style={inputStyle}
              />
              <button onClick={joinRoom} disabled={!joinCode.trim()} style={{ ...btnSmall, opacity: joinCode.trim() ? 1 : 0.4 }}>Join</button>
            </div>
          </div>
        </>
      ) : (
        <>
          <div style={{ padding: '10px', background: 'rgba(124,110,245,0.06)', borderRadius: 8, border: '1px solid var(--border)' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-0)', fontFamily: 'var(--font-mono)' }}>
              {ownerName === user?.name ? '🟢 Your Room' : `🟢 ${ownerName}'s Room`}
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)', marginTop: 2 }}>
              {peers.length + 1} online · Files sync in real-time
            </div>
          </div>

          {inviteCode && (
            <div>
              <div style={sectionLabel}>Invite link</div>
              <div style={{ padding: '6px 8px', borderRadius: 6, background: 'var(--bg-2)', border: '1px solid var(--border)', fontSize: 9, fontFamily: 'var(--font-mono)', color: 'var(--accent)', wordBreak: 'break-all', marginBottom: 6 }}>
                {window.location.origin}?collab={inviteCode}
              </div>
              <button onClick={copyInvite} style={{ ...btnStyle, fontSize: 10 }}>
                {copied ? '✓ Copied!' : '📋 Copy invite link'}
              </button>
            </div>
          )}

          <div style={{ height: 1, background: 'var(--border)' }} />

          <div>
            <div style={sectionLabel}>Online ({peers.length + 1})</div>
            <div style={peerRow}>
              <div style={{ ...avatarStyle, background: 'var(--accent)' }}>{user?.name?.[0]?.toUpperCase() ?? '?'}</div>
              <span style={{ fontSize: 11, color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>
                {user?.name} <span style={{ color: 'var(--text-2)', fontSize: 9 }}>(you)</span>
              </span>
            </div>
            {peers.map((p) => (
              <div key={p.userId} style={peerRow}>
                <div style={{ ...avatarStyle, background: p.color }}>{p.displayName[0]?.toUpperCase() ?? '?'}</div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--text-0)', fontFamily: 'var(--font-mono)', fontWeight: 600 }}>{p.displayName}</div>
                  {p.activeFile && <div style={{ fontSize: 9, color: 'var(--text-2)', fontFamily: 'var(--font-mono)' }}>{p.activeFile}</div>}
                </div>
              </div>
            ))}
          </div>

          <div style={{ height: 1, background: 'var(--border)' }} />
          <button onClick={leaveRoom} style={{ ...btnStyle, borderColor: 'var(--red)', color: 'var(--red)', background: 'rgba(240,112,112,0.06)' }}>
            Leave Room
          </button>
        </>
      )}
    </div>
  )
}

const sectionLabel: React.CSSProperties = {
  fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--text-2)',
  textTransform: 'uppercase', fontFamily: 'var(--font-mono)', marginBottom: 6,
}
const btnStyle: React.CSSProperties = {
  padding: '7px 12px', borderRadius: 6, border: '1px solid var(--accent)',
  background: 'rgba(124,110,245,0.06)', color: 'var(--accent)',
  cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
  display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center', width: '100%',
}
const btnSmall: React.CSSProperties = {
  padding: '5px 10px', borderRadius: 6, border: '1px solid var(--accent)',
  background: 'rgba(124,110,245,0.06)', color: 'var(--accent)',
  cursor: 'pointer', fontFamily: 'var(--font-mono)', fontSize: 10, fontWeight: 700,
}
const inputStyle: React.CSSProperties = {
  padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border-2)',
  background: 'var(--bg-2)', color: 'var(--text-0)', fontFamily: 'var(--font-mono)',
  fontSize: 10, outline: 'none', flex: 1,
}
const peerRow: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0',
}
const avatarStyle: React.CSSProperties = {
  width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center',
  justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff',
  fontFamily: 'var(--font-display)', flexShrink: 0,
}
